import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import confetti from "canvas-confetti";
import { Award, Download, Link2, Printer, Star, X } from "lucide-react";
import { toast } from "sonner";
import GhostButton from "./GhostButton";
import GoldButton from "./GoldButton";
import CertificateSVG, {
  exportCertificatePDF,
  exportCertificateSVG,
} from "./CertificateSVG";
import { claimReward } from "../lib/rewards";
import { useWallet } from "../hooks/useWallet";

/**
 * CertificationOverlay — 3-tier reward modal.
 *
 *   silver (8.0-8.99) : small card, silver badge, no confetti, no NXT.
 *   gold   (9.0-9.99) : larger modal, gold badge + particle overlay, no NXT.
 *   master (>=10.0)   : full-screen confetti burst, SVG certificate preview,
 *                       auto-claims +5 NXT via /api/audits/{id}/claim-reward,
 *                       "Download Certificate (SVG)" available.
 *
 *  Idempotency: the backend's claim endpoint is idempotent by audit_id. If
 *  the user re-opens the overlay for an already-claimed audit, `alreadyClaimed`
 *  will be true and NXT is not re-credited.
 */
export function CertificationOverlay({ open, tier, audit, onClose }) {
  const { t } = useTranslation();
  const { user } = useWallet();
  const svgRef = useRef(null);
  const [claim, setClaim] = useState(null); // {signature_hash, already_claimed, nxt_credited}
  const [claiming, setClaiming] = useState(false);

  // Backend surface is inconsistent: /api/audit/{quick,deep} responses expose
  // `audit_id`, while GET /api/audits/{id} exposes `id`. Both are the same
  // logical value.
  const auditId = audit?.audit_id || audit?.id;

  // Master-tier auto-claim on open — SKIPPED when running the [DEMO_MODE]
  // flow so we never hit the backend and never credit any NXT. The claim
  // object is synthesized locally with a fixed DEMO signature.
  useEffect(() => {
    if (!open || tier !== "master" || !auditId) return;
    if (audit?.__demo_mode) {
      setClaim({
        audit_id: auditId,
        tier: "master",
        nxt_credited: 0,
        signature_hash: audit.__demo_signature || "DEMO-A3F2-B9C1-E7D4-8F62",
        claimed_at: new Date().toISOString(),
        already_claimed: false,
        __demo_mode: true,
      });
      toast(t("cert.demo.toast"));
      return;
    }
    let live = true;
    (async () => {
      setClaiming(true);
      const res = await claimReward(auditId);
      setClaiming(false);
      if (!live) return;
      if (res.ok) {
        setClaim(res.data);
      }
    })();
    return () => {
      live = false;
    };
  }, [open, tier, auditId, audit?.__demo_mode, audit?.__demo_signature, t]);

  // Confetti threshold (Phase 7): fires for score >= 8.9 (covers upper-silver,
  // gold and master). Guarded with Number.isFinite so undefined / NaN / string
  // "abc" values never fire.
  useEffect(() => {
    if (!open) return;
    const score = parseFloat(audit?.nexa_score ?? 0);
    const shouldConfetti = Number.isFinite(score) && score >= 8.9;
    if (!shouldConfetti) return;
    const colors = ["#D4AF37", "#F4D77A", "#1E40AF", "#22D3EE", "#FFFFFF"];
    const fire = (opts) =>
      confetti({
        particleCount: 90,
        spread: 75,
        startVelocity: 45,
        colors,
        zIndex: 60,
        disableForReducedMotion: true,
        ...opts,
      });
    const t0 = setTimeout(() => fire({ origin: { x: 0.2, y: 0.4 } }), 50);
    const t1 = setTimeout(() => fire({ origin: { x: 0.8, y: 0.4 } }), 500);
    const t2 = setTimeout(
      () => fire({ particleCount: 140, spread: 100, origin: { x: 0.5, y: 0.5 } }),
      1200,
    );
    return () => {
      clearTimeout(t0);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [open, audit?.nexa_score]);

  // Escape to close
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const onShare = useCallback(async () => {
    const sig = claim?.signature_hash || "";
    const verifyUrl = `https://nexalitics.pro/verify?hash=${encodeURIComponent(sig)}`;
    try {
      if (navigator.clipboard) await navigator.clipboard.writeText(verifyUrl);
    } catch {
      /* clipboard denied — toast still fires */
    }
    toast.success(t("cert.action.link_copied"));
  }, [claim?.signature_hash, t]);

  const onDownload = useCallback(() => {
    if (!svgRef.current) return;
    const sig = (claim?.signature_hash || "cert").replace(/[^A-Z0-9-]/gi, "");
    const isDemo = !!audit?.__demo_mode;
    const filename = isDemo
      ? `nexalytics-DEMO-certificate-${sig}.svg`
      : `nexalytics-certificate-${sig}.svg`;
    exportCertificateSVG(svgRef.current, filename, { demoMode: isDemo, signature: sig });
  }, [claim?.signature_hash, audit?.__demo_mode]);

  /** Phase 13: Download Executive PDF — jsPDF + svg2pdf.js. Falls back to
   *  window.print() on failure (same behaviour as the Phase 7 Print button)
   *  so the user still gets a tangible artefact. */
  const onDownloadPDF = useCallback(async () => {
    if (!svgRef.current) return;
    const sig = (claim?.signature_hash || "cert").replace(/[^A-Z0-9-]/gi, "");
    const isDemo = !!audit?.__demo_mode;
    const filename = isDemo
      ? `nexalytics-DEMO-certificate-${sig}.pdf`
      : `nexalytics-certificate-${sig}.pdf`;
    try {
      await exportCertificatePDF(svgRef.current, filename);
    } catch (err) {
      /* eslint-disable-next-line no-console */
      console.error("[cert] PDF export failed, falling back to print:", err);
      toast.error(t("cert.pdf.failed"));
      if (typeof window !== "undefined") window.print();
    }
  }, [claim?.signature_hash, audit?.__demo_mode, t]);

  // Print Certificate (Phase 7). Triggers browser print dialog. `@media print`
  // rules in index.css hide everything except `.cert-print-root` so the
  // certificate fills the printed page. Available whenever score >= 9.0.
  const onPrint = useCallback(() => {
    if (typeof window === "undefined") return;
    window.print();
  }, []);

  if (!open || !tier) return null;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="cert-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 overflow-y-auto print:bg-transparent print:backdrop-blur-none print:p-0 print:overflow-visible print:static"
          onClick={onClose}
          data-testid={`cert-overlay-${tier}`}
        >
          <div className="w-full max-w-3xl" onClick={(e) => e.stopPropagation()}>
            {tier === "silver" && (
              <SilverCard audit={audit} onClose={onClose} />
            )}

            {tier === "gold" && (
              <GoldCard
                audit={audit}
                user={user}
                claim={claim}
                onClose={onClose}
                onShare={onShare}
                onPrint={onPrint}
                onDownloadPDF={onDownloadPDF}
                svgRef={svgRef}
              />
            )}

            {tier === "master" && (
              <MasterCard
                audit={audit}
                user={user}
                claim={claim}
                claiming={claiming}
                onClose={onClose}
                onShare={onShare}
                onDownload={onDownload}
                onDownloadPDF={onDownloadPDF}
                onPrint={onPrint}
                svgRef={svgRef}
              />
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ------------------------- Silver ------------------------------------------

function SilverCard({ audit, onClose }) {
  const { t } = useTranslation();
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94 }}
      transition={{ duration: 0.35, ease: [0.22, 0.9, 0.36, 1] }}
      className="mx-auto max-w-sm rounded-3xl border border-white/12 bg-[#0a0b0f]/95 p-7 text-center"
      data-testid="cert-modal-silver"
    >
      <button
        onClick={onClose}
        aria-label={t("common.close")}
        className="absolute right-6 top-6 text-white/50 hover:text-white transition-colors"
        data-testid="cert-close-btn"
      >
        <X className="h-5 w-5" />
      </button>

      <div
        className="mx-auto mb-4 inline-flex h-24 w-24 items-center justify-center rounded-full"
        style={{
          background:
            "radial-gradient(circle at 30% 30%, #F5F5F5 0%, #C0C0C0 40%, #808080 100%)",
          boxShadow:
            "0 8px 20px -6px rgba(192,192,192,0.55), inset 0 -6px 12px rgba(0,0,0,0.35), inset 0 4px 10px rgba(255,255,255,0.55)",
        }}
      >
        <Star className="h-10 w-10 text-[#4A4A4A]" strokeWidth={1.5} fill="#DADADA" />
      </div>

      <div className="text-xs uppercase tracking-[0.22em] text-white/50">
        {t("cert.tier.silver.title")}
      </div>
      <div className="mt-2 font-display text-4xl text-white tabular-nums">
        {(audit?.nexa_score ?? 0).toFixed(1)}
        <span className="ml-1 text-base text-white/40">/ 10</span>
      </div>

      <div className="mt-6 flex justify-center">
        <GhostButton onClick={onClose} data-testid="cert-continue-btn">
          {t("cert.continue")}
        </GhostButton>
      </div>
    </motion.div>
  );
}

// ------------------------- Gold --------------------------------------------

const GOLD_PARTICLES = Array.from({ length: 18 }).map((_, i) => {
  const angle = (i / 18) * Math.PI * 2;
  const r = 140 + Math.random() * 60;
  return {
    x: Math.cos(angle) * r,
    y: Math.sin(angle) * r,
    delay: 0.4 + i * 0.04,
  };
});

function GoldCard({ audit, user, claim, onClose, onShare, onPrint, onDownloadPDF, svgRef }) {
  const { t } = useTranslation();
  const score = parseFloat(audit?.nexa_score ?? 0);
  const showCertificate = Number.isFinite(score) && score >= 9.0;
  const certLabels = buildCertLabels(t, score);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.94 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.94 }}
      transition={{ duration: 0.4, ease: [0.22, 0.9, 0.36, 1] }}
      className="relative mx-auto max-w-3xl rounded-3xl border border-[#D4AF37]/40 bg-[#08090f]/95 p-8 text-center overflow-hidden"
      data-testid="cert-modal-gold"
    >
      <button
        onClick={onClose}
        aria-label={t("common.close")}
        className="absolute right-6 top-6 text-white/50 hover:text-white transition-colors z-10 print:hidden"
        data-testid="cert-close-btn"
      >
        <X className="h-5 w-5" />
      </button>

      {/* Particle overlay (local, framer-motion) */}
      <div className="pointer-events-none absolute inset-0 flex items-center justify-center print:hidden" aria-hidden>
        {GOLD_PARTICLES.map((p, i) => (
          <motion.span
            key={i}
            className="absolute h-1.5 w-1.5 rounded-full"
            style={{ background: "#FFD700", boxShadow: "0 0 8px #FFD700" }}
            initial={{ x: 0, y: 0, opacity: 0, scale: 0.3 }}
            animate={{ x: p.x, y: p.y, opacity: [0, 1, 0], scale: [0.3, 1, 0.6] }}
            transition={{ duration: 1.8, delay: p.delay, ease: "easeOut" }}
          />
        ))}
      </div>

      <motion.div
        initial={{ scale: 0.4, opacity: 0 }}
        animate={{ scale: [0.4, 1.08, 1], opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative mx-auto mb-5 inline-flex h-24 w-24 items-center justify-center rounded-full print:hidden"
        style={{
          background:
            "radial-gradient(circle at 35% 30%, #FFF4B5 0%, #FFD700 45%, #B8860B 100%)",
          boxShadow:
            "0 0 40px 8px rgba(212,175,55,0.55), inset 0 -8px 18px rgba(0,0,0,0.35), inset 0 6px 14px rgba(255,255,255,0.55)",
        }}
      >
        <Award className="h-12 w-12 text-[#7A5713]" strokeWidth={1.6} />
      </motion.div>

      <div className="relative print:hidden">
        <div className="text-xs uppercase tracking-[0.24em] text-[#D4AF37]/85">
          {t("cert.tier.gold.title")}
        </div>
        <div className="mt-2 font-display text-5xl text-white tabular-nums">
          {(audit?.nexa_score ?? 0).toFixed(1)}
          <span className="ml-1 text-lg text-white/40">/ 10</span>
        </div>
      </div>

      {/* Certificate SVG preview (Phase 7: rendered for score >= 9.0) */}
      {showCertificate ? (
        <div className="cert-print-root mt-6 rounded-2xl border border-[#D4AF37]/25 bg-[#FBF8F0] p-3 shadow-[0_20px_60px_-30px_rgba(212,175,55,0.6)] print:block print:static print:w-full print:mt-0 print:border-0 print:shadow-none">
          <CertificateSVG
            ref={svgRef}
            userName={user?.name || "Nexa Auditor"}
            promptText={audit?.prompt || ""}
            signatureHash={claim?.signature_hash || "————-————-————-————"}
            claimedAt={claim?.claimed_at || new Date().toISOString()}
            tier="gold"
            showRewardLine={false}
            labels={certLabels}
          />
        </div>
      ) : null}

      <div className="relative mt-6 flex flex-wrap justify-center gap-2 print:hidden">
        {showCertificate ? (
          <>
            <GoldButton
              onClick={onDownloadPDF}
              data-testid="cert-download-pdf-btn"
              className="gap-2"
            >
              <Download className="h-4 w-4" />
              {t("cert.action.download_pdf")}
            </GoldButton>
            <GoldButton
              onClick={onPrint}
              data-testid="cert-print-btn"
              className="gap-2"
              style={{ background: "#FFD700", color: "#0D0D0D" }}
            >
              <Printer className="h-4 w-4" />
              {t("cert.print")}
            </GoldButton>
          </>
        ) : null}
        <GhostButton onClick={onShare} data-testid="cert-share-btn" className="gap-2">
          <Link2 className="h-4 w-4" />
          {t("cert.action.share")}
        </GhostButton>
        <GhostButton onClick={onClose} data-testid="cert-continue-btn">
          {t("cert.continue")}
        </GhostButton>
      </div>
    </motion.div>
  );
}

// ------------------------- Master ------------------------------------------

function MasterCard({
  audit,
  user,
  claim,
  claiming,
  onClose,
  onShare,
  onDownload,
  onDownloadPDF,
  onPrint,
  svgRef,
}) {
  const { t } = useTranslation();
  const score = parseFloat(audit?.nexa_score ?? 0);
  const showCertificate = Number.isFinite(score) && score >= 9.0;
  const certLabels = buildCertLabels(t, score);
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.4, ease: [0.22, 0.9, 0.36, 1] }}
      className="relative mx-auto w-full max-w-3xl rounded-3xl border border-[#D4AF37]/45 bg-[#050609]/95 p-6 sm:p-8"
      data-testid="cert-modal-master"
    >
      <button
        onClick={onClose}
        aria-label={t("common.close")}
        className="absolute right-6 top-6 z-10 text-white/50 hover:text-white transition-colors print:hidden"
        data-testid="cert-close-btn"
      >
        <X className="h-5 w-5" />
      </button>

      <div className="text-center print:hidden">
        <div className="text-xs uppercase tracking-[0.24em] text-[#D4AF37]/85">
          {t("cert.tier.master.title")}
        </div>
        <div className="mt-2 flex items-baseline justify-center gap-2">
          <span className="font-display text-6xl text-white tabular-nums">
            {(audit?.nexa_score ?? 10).toFixed(1)}
          </span>
          <span className="text-lg text-white/40">/ 10</span>
        </div>
      </div>

      {/* Certificate SVG preview (Phase 7 gate: score >= 9.0) */}
      {showCertificate ? (
        <div className="cert-print-root mt-6 rounded-2xl border border-[#D4AF37]/25 bg-[#FBF8F0] p-3 shadow-[0_20px_60px_-30px_rgba(212,175,55,0.6)] print:block print:static print:w-full print:mt-0 print:border-0 print:shadow-none">
          <CertificateSVG
            ref={svgRef}
            userName={user?.name || "Nexa Auditor"}
            promptText={audit?.prompt || ""}
            signatureHash={claim?.signature_hash || "————-————-————-————"}
            claimedAt={claim?.claimed_at || new Date().toISOString()}
            tier="master"
            showRewardLine={!audit?.__demo_mode}
            labels={certLabels}
          />
        </div>
      ) : null}

      {/* Reward line */}
      <div
        className="mt-5 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#D4AF37]/30 bg-[#D4AF37]/[0.06] px-4 py-3 print:hidden"
        data-testid="cert-reward-line"
      >
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#D4AF37]/80">
            {t("cert.reward.label")}
          </div>
          <div className="mt-0.5 text-sm text-white">
            {claiming
              ? t("common.loading")
              : claim
                ? claim.__demo_mode
                  ? t("cert.demo.rewardLine")
                  : claim.already_claimed
                    ? t("cert.reward.alreadyClaimed", { n: claim.nxt_credited })
                    : t("cert.reward.credited", { n: claim.nxt_credited })
                : t("cert.reward.pending")}
          </div>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-[0.22em] text-white/50">
            {t("cert.signature.label")}
          </div>
          <div
            className="mt-0.5 font-mono text-sm tabular-nums text-[#22D3EE]"
            data-testid="cert-signature-hash"
          >
            {claim?.signature_hash || "—"}
          </div>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap justify-center gap-3 print:hidden">
        <GoldButton
          onClick={onDownloadPDF}
          disabled={!claim}
          data-testid="cert-download-pdf-btn"
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          {t("cert.action.download_pdf")}
        </GoldButton>
        {showCertificate ? (
          <GoldButton
            onClick={onPrint}
            data-testid="cert-print-btn"
            className="gap-2"
            style={{ background: "#FFD700", color: "#0D0D0D" }}
          >
            <Printer className="h-4 w-4" />
            {t("cert.print")}
          </GoldButton>
        ) : null}
        <GhostButton onClick={onShare} data-testid="cert-share-btn" className="gap-2">
          <Link2 className="h-4 w-4" />
          {t("cert.action.share")}
        </GhostButton>
        <GhostButton
          onClick={onDownload}
          disabled={!claim}
          data-testid="cert-download-svg-btn"
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          {t("cert.download")}
        </GhostButton>
        <GhostButton onClick={onClose} data-testid="cert-close-btn-bottom">
          {t("cert.close")}
        </GhostButton>
      </div>
    </motion.div>
  );
}

/** Build the localized label bundle passed into <CertificateSVG />. The
 *  score value is embedded here so the SVG can render it inline — this
 *  lets us keep the component declarative (no i18n hooks inside the SVG). */
function buildCertLabels(t, score) {
  const scoreValue = Number.isFinite(score) ? score.toFixed(1) : null;
  return {
    header: t("cert.header.title"),
    subtitle: t("cert.subtitle"),
    statement: t("cert.statement"),
    date: t("cert.date"),
    hash: t("cert.hash"),
    by: t("cert.by"),
    signatureAuthorized: t("cert.signature.authorized"),
    qrScan: t("cert.qr.label"),
    certified: t("cert.badge.certified"),
    rewardLine: t("cert.reward.credited", { n: 5 }),
    scoreTotal: "/10",
    scoreValue,
  };
}

export default CertificationOverlay;
